import React, { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { CatalogProduct } from '../data/catalog';
import { CheckoutLink, EnrichedCatalogProduct, PriceHistory } from '../types';
import { getCheckoutLinks, getPriceHistory, getEnrichedProduct } from '../utils/api';
import { PriceHistoryGraph } from './PriceHistoryGraph';
import { useTrackedStores } from '../hooks/useTrackedStores';

interface Props {
  product: CatalogProduct | null;
  onClose: () => void;
}

const REGION_FLAGS: Record<string, string> = {
  ES: '🇪🇸', NL: '🇳🇱', DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', UK: '🇬🇧', GB: '🇬🇧',
  BE: '🇧🇪', US: '🇺🇸', EU: '🇪🇺', JP: '🇯🇵',
};

export function ProductDrawer({ product, onClose }: Props) {
  const [links, setLinks] = useState<CheckoutLink[]>([]);
  const [stockVerifiedAt, setStockVerifiedAt] = useState<string | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceHistory | null>(null);
  const [enriched, setEnriched] = useState<EnrichedCatalogProduct | null>(null);
  const [loading, setLoading] = useState(false);
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [tab, setTab] = useState<'overview' | 'stores' | 'rarity' | 'history'>('overview');
  const { trackStore, untrackStore, isTracked } = useTrackedStores();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!product) return;
    setLoading(true);
    setLinks([]);
    setStockVerifiedAt(null);
    setPriceHistory(null);
    setEnriched(null);
    setTab('overview');
    setRegionFilter('all');

    Promise.all([
      getCheckoutLinks(product.id).catch(() => ({ links: [] as CheckoutLink[], stockVerifiedAt: null })),
      getPriceHistory(product.id).catch(() => null),
      getEnrichedProduct(product.id).catch(() => null),
    ]).then(([checkoutData, history, enrichedData]) => {
      setLinks(checkoutData.links ?? []);
      setStockVerifiedAt(checkoutData.stockVerifiedAt ?? null);
      setPriceHistory(history);
      setEnriched(enrichedData);
      setLoading(false);
    });
  }, [product?.id]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!product) return;
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [product, handleKeyDown]);

  if (!product) return null;

  const isUpcoming = !product.releaseDate || new Date(product.releaseDate) > new Date();
  const allRegions = Array.from(new Set(links.map((l) => l.store.region)));
  const visibleLinks = regionFilter === 'all'
    ? links
    : links.filter((l) => l.store.region === regionFilter || l.store.country === regionFilter);

  // Normalize price to EUR-equivalent for cross-currency comparison
  const eurEquivalent = (l: CheckoutLink): number => {
    if (l.estimatedPrice == null) return Infinity;
    if (l.currency === 'GBP') return l.estimatedPrice * 1.18;
    if (l.currency === 'USD') return l.estimatedPrice * 0.92;
    return l.estimatedPrice;
  };

  // Best-match ordering: saved stores first, then by price (preorders ranked alongside in-stock)
  const bestMatchSort = (a: CheckoutLink, b: CheckoutLink) => {
    const aSaved = isTracked(a.store.id) ? 0 : 1;
    const bSaved = isTracked(b.store.id) ? 0 : 1;
    if (aSaved !== bSaved) return aSaved - bSaved;
    return eurEquivalent(a) - eurEquivalent(b);
  };

  const sortedLinks = [...visibleLinks].sort(bestMatchSort);

  const eurLinks = links.filter((l) => l.currency === 'EUR' && l.estimatedPrice !== null);
  const lowestEur = eurLinks.length > 0 ? Math.min(...eurLinks.map((l) => l.estimatedPrice!)) : null;

  // Top 5 stores for Overview tab — prefer saved stores, then cheapest available
  const topLinks = [...links].sort(bestMatchSort).slice(0, 5);

  const lineColor = product.line === 'fusion_world' ? '#f97316' : '#8b5cf6';

  return (
    <div
      style={{
        ...s.backdrop,
        alignItems: isMobile ? 'flex-end' : 'center',
        padding: isMobile ? 0 : '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...s.modal,
          ...(isMobile ? {
            width: '100%',
            maxWidth: '100%',
            height: '92vh',
            maxHeight: '92vh',
            borderRadius: '20px 20px 0 0',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
          } : {}),
        }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Sticky header ─────────────────────────────────── */}
        <div style={{ ...s.header, padding: isMobile ? '14px 16px 12px' : '20px 24px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1, minWidth: 0 }}>
            <div style={{ ...s.setCodeBox, background: lineColor + '20', borderColor: lineColor + '50' }}>
              <code style={{ fontSize: '13px', fontWeight: 800, color: lineColor, letterSpacing: '0.04em' }}>
                {product.setCode}
              </code>
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 style={s.productTitle}>{product.title}</h2>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '5px' }}>
                <span style={{ ...s.pill, background: lineColor + '20', color: lineColor }}>
                  {product.line === 'fusion_world' ? 'Fusion World' : 'Masters'}
                </span>
                <span style={{ ...s.pill, background: '#2e3347', color: '#8a93b0' }}>
                  {product.productType}
                </span>
                {isUpcoming && (
                  <span style={{ ...s.pill, background: '#3b82f620', color: '#3b82f6' }}>Upcoming</span>
                )}
                {lowestEur && (
                  <span style={{ ...s.pill, background: '#22c55e20', color: '#22c55e' }}>
                    From €{lowestEur.toFixed(0)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={s.closeBtn} aria-label="Close">✕</button>
        </div>

        {/* ── Tab bar ───────────────────────────────────────── */}
        <div style={{ ...s.tabBar, padding: isMobile ? '0 8px' : '0 24px' }} className="scroll-x">
          {(['overview', 'stores', 'rarity', 'history'] as const).map((t) => (
            <button
              key={t}
              style={{ ...s.tabBtn, ...(tab === t ? s.tabBtnActive : {}), ...(isMobile ? { padding: '12px 12px', fontSize: '12px' } : {}) }}
              onClick={() => setTab(t)}
            >
              {t === 'overview' ? 'Overview'
                : t === 'stores' ? `Stores (${links.length})`
                : t === 'rarity' ? 'Rarity'
                : 'Price History'}
            </button>
          ))}
        </div>

        {/* ── Scrollable body ───────────────────────────────── */}
        <div style={{ ...s.body, WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>

          {/* OVERVIEW */}
          {tab === 'overview' && (
            <div style={s.section}>
              {/* Key stats */}
              <div style={s.statsGrid}>
                <Stat label="Release date" value={product.releaseDate ?? 'TBA'} sub={product.releaseDateStatus === 'Unconfirmed' ? 'Unconfirmed' : undefined} subColor="#eab308" />
                {product.msrpPerPack && <Stat label="MSRP / pack" value={`$${product.msrpPerPack}`} color="#f97316" />}
                {product.msrpPerPack && product.packsPerBox > 1 && (
                  <Stat label={`Box (${product.packsPerBox} packs)`} value={`~$${(product.msrpPerPack * product.packsPerBox).toFixed(0)}`} color="#f97316" />
                )}
                {lowestEur && <Stat label="Lowest listed (EU)" value={`€${lowestEur.toFixed(2)}`} color="#22c55e" />}
              </div>

              {/* Description */}
              <p style={s.description}>{enriched?.officialDescription ?? product.description}</p>

              {/* Box contents */}
              {enriched?.boxContents && enriched.boxContents.length > 0 && (
                <div style={s.boxContents}>
                  <div style={s.subHeading}>Box contents</div>
                  <div style={s.chipRow}>
                    {enriched.boxContents.map((item, i) => (
                      <span key={i} style={s.contentChip}>{item}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Region availability */}
              {enriched?.regionAvailability && (
                <div>
                  <div style={s.subHeading}>Ships to</div>
                  <div style={s.chipRow}>
                    {enriched.regionAvailability.map((r) => (
                      <span key={r} style={s.regionChip}>{REGION_FLAGS[r] ?? ''} {r}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Top stores preview */}
              {(topLinks.length > 0 || loading) && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={s.subHeading}>Top stores</div>
                    <button style={s.showAllBtn} onClick={() => setTab('stores')}>
                      View all {links.length} stores →
                    </button>
                  </div>
                  {loading ? (
                    <p style={{ color: '#8a93b0', fontSize: '13px' }}>Loading stores…</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {topLinks.map((link, i) => (
                        <StoreRow
                          key={i}
                          link={link}
                          isLowest={link.estimatedPrice === lowestEur && link.currency === 'EUR'}
                          isTracked={isTracked(link.store.id)}
                          onToggleTrack={() => isTracked(link.store.id) ? untrackStore(link.store.id) : trackStore(link.store, link.url)}
                          isMobile={isMobile}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STORES */}
          {tab === 'stores' && (
            <div style={s.section}>
              <VerifiedBanner verifiedAt={stockVerifiedAt} />

              <div style={s.chipRow}>
                {['all', ...allRegions].map((r) => (
                  <button
                    key={r}
                    style={{ ...s.filterChip, ...(regionFilter === r ? s.filterChipActive : {}) }}
                    onClick={() => setRegionFilter(r)}
                  >
                    {r === 'all' ? 'All regions' : `${REGION_FLAGS[r] ?? ''} ${r}`}
                  </button>
                ))}
              </div>

              {loading && <p style={{ color: '#8a93b0', fontSize: '13px', padding: '12px 0' }}>Loading stores…</p>}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {sortedLinks.map((link, i) => (
                  <StoreRow
                    key={i}
                    link={link}
                    isLowest={link.estimatedPrice === lowestEur && link.currency === 'EUR'}
                    isTracked={isTracked(link.store.id)}
                    onToggleTrack={() => isTracked(link.store.id) ? untrackStore(link.store.id) : trackStore(link.store, link.url)}
                    showSpecialties
                    isMobile={isMobile}
                    stockVerified={stockVerifiedAt !== null}
                  />
                ))}
                {!loading && sortedLinks.length === 0 && (
                  <p style={{ color: '#8a93b0', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No stores for this region.</p>
                )}
              </div>
            </div>
          )}

          {/* RARITY */}
          {tab === 'rarity' && (
            <div style={s.section}>
              {enriched?.rarityBreakdown ? (
                <>
                  {enriched.rarityBreakdown.filter((r) => r.count > 0 || r.name.toLowerCase().includes('rare')).map((rarity) => {
                    const total = enriched.rarityBreakdown.reduce((sum, r) => sum + r.count, 0);
                    const pct = total > 0 ? (rarity.count / total) * 100 : 0;
                    return (
                      <div key={rarity.name} style={{ marginBottom: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: rarity.color }}>{rarity.name}</span>
                          <span style={{ fontSize: '12px', color: '#8a93b0' }}>
                            {rarity.count > 0 ? `~${rarity.count} per box` : 'Chase card — varies by case'}
                          </span>
                        </div>
                        <div style={{ height: '8px', background: '#2e3347', borderRadius: '4px', overflow: 'hidden' }}>
                          {rarity.count > 0 && (
                            <div style={{ height: '100%', width: `${Math.max(pct, 1)}%`, background: rarity.color, borderRadius: '4px' }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <p style={{ fontSize: '12px', color: '#8a93b0', marginTop: '8px' }}>
                    Approximate distribution per box ({product.packsPerBox} packs × 12 cards). Chase card rates vary by case.
                  </p>
                </>
              ) : (
                <p style={{ color: '#8a93b0', fontSize: '13px' }}>Rarity data not available for this product type.</p>
              )}
            </div>
          )}

          {/* HISTORY */}
          {tab === 'history' && (
            <div style={s.section}>
              {priceHistory?.lowestEver && (
                <div style={s.statsGrid}>
                  <Stat label="All-time low" value={`€${priceHistory.lowestEver.price.toFixed(2)}`} color="#22c55e" />
                  {priceHistory.highestEver && (
                    <Stat label="All-time high" value={`€${priceHistory.highestEver.price.toFixed(2)}`} color="#ef4444" />
                  )}
                </div>
              )}
              <PriceHistoryGraph points={priceHistory?.points ?? []} currency="EUR" width={640} height={160} />
              <p style={{ fontSize: '12px', color: '#8a93b0', marginTop: '12px' }}>
                EUR prices only. History accumulates each time you search for this product.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Stat({ label, value, sub, color, subColor }: { label: string; value: string; sub?: string; color?: string; subColor?: string }) {
  return (
    <div style={s.statBox}>
      <span style={s.statLabel}>{label}</span>
      <span style={{ ...s.statValue, color: color ?? '#e8eaf0' }}>{value}</span>
      {sub && <span style={{ fontSize: '11px', color: subColor ?? '#8a93b0', marginTop: '2px' }}>{sub}</span>}
    </div>
  );
}

function buttonLabelFor(link: CheckoutLink, verified: boolean | undefined): string {
  if (link.stockStatus === 'Coming Soon') return 'Preorder';
  if (link.stockStatus === 'Not Yet Listed') return 'Search store';
  if (verified && link.stockStatus === 'Available') return 'Buy →';
  if (verified && link.stockStatus === 'Out of Stock') return 'Sold out';
  // Released but unverified — point at store, no false promise
  return 'View at store';
}

function buttonStyleFor(link: CheckoutLink, verified: boolean | undefined): React.CSSProperties {
  if (link.stockStatus === 'Coming Soon') return s.preorderBtn;
  if (verified && link.stockStatus === 'Available') return s.buyBtn;
  if (verified && link.stockStatus === 'Out of Stock') return s.oosBtn;
  return s.viewBtn;
}

function VerifiedBanner({ verifiedAt }: { verifiedAt: string | null }) {
  if (!verifiedAt) {
    return (
      <div style={s.unverifiedBanner}>
        Stock not yet verified for this product. Status below reflects release
        date only — confirm on the store before buying.
      </div>
    );
  }
  const ageMin = Math.round((Date.now() - new Date(verifiedAt).getTime()) / 60000);
  const label =
    ageMin < 60 ? `${ageMin} min ago`
      : ageMin < 1440 ? `${Math.round(ageMin / 60)} h ago`
      : `${Math.round(ageMin / 1440)} d ago`;
  return <div style={s.verifiedBanner}>● Stock verified {label}</div>;
}

interface StoreRowProps {
  link: CheckoutLink;
  isLowest: boolean;
  isTracked: boolean;
  onToggleTrack: () => void;
  showSpecialties?: boolean;
  isMobile?: boolean;
  stockVerified?: boolean;
}

function StoreRow({ link, isLowest, isTracked, onToggleTrack, showSpecialties, isMobile, stockVerified }: StoreRowProps) {
  const REGION_FLAGS: Record<string, string> = {
    ES: '🇪🇸', NL: '🇳🇱', DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹',
    UK: '🇬🇧', BE: '🇧🇪', US: '🇺🇸', EU: '🇪🇺', JP: '🇯🇵',
  };
  const sym = link.currency === 'EUR' ? '€' : link.currency === 'GBP' ? '£' : '$';

  return (
    <div style={{ ...s.storeRow, ...(isTracked ? s.storeRowTracked : {}), flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '12px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: '13px', color: '#e8eaf0' }}>{link.store.name}</span>
          <span style={{ fontSize: '12px', color: '#8a93b0' }}>{REGION_FLAGS[link.store.region] ?? ''} {link.store.region}</span>
          {stockVerified && link.stockStatus === 'Available' && <span style={s.availableBadge}>● in stock</span>}
          {stockVerified && link.stockStatus === 'Out of Stock' && <span style={s.oosBadge}>● sold out</span>}
          {!stockVerified && link.stockStatus === 'Available' && <span style={s.unlistedBadge}>released · check store</span>}
          {link.stockStatus === 'Coming Soon' && <span style={s.preorderBadge}>preorder</span>}
          {link.stockStatus === 'Not Yet Listed' && <span style={s.unlistedBadge}>not listed</span>}
          {isLowest && <span style={s.bestBadge}>best price</span>}
          {link.store.heavilyProtected && (
            <span style={s.shieldBadge} title="May require verification to access">🛡 verify</span>
          )}
          {isTracked && <span style={s.trackedBadge}>★ saved</span>}
        </div>
        {showSpecialties && link.store.specialties.length > 0 && (
          <div style={{ fontSize: '11px', color: '#8a93b0', marginTop: '2px' }}>
            {link.store.specialties.slice(0, 3).join(' · ')}
          </div>
        )}
        {link.notes && <div style={{ fontSize: '11px', color: '#8a93b0', fontStyle: 'italic', marginTop: '2px' }}>{link.notes}</div>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
        {!isMobile && (
          <span style={{ fontSize: '12px', color: '#8a93b0' }}>
            {'★'.repeat(Math.round(link.store.rating))}
          </span>
        )}

        <span style={{ fontWeight: 700, fontSize: '15px', color: link.estimatedPrice ? '#e8eaf0' : '#8a93b0' }}>
          {link.estimatedPrice ? `${sym}${link.estimatedPrice.toFixed(0)}` : '—'}
        </span>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <a href={link.url} target="_blank" rel="noopener noreferrer"
            style={{ ...buttonStyleFor(link, stockVerified), ...(isMobile ? { padding: '10px 16px', fontSize: '13px' } : {}) }}>
            {buttonLabelFor(link, stockVerified)}
          </a>
          <button
            title={isTracked ? 'Remove from saved stores' : 'Save store'}
            style={{ ...s.starBtn, ...(isTracked ? s.starBtnActive : {}), ...(isMobile ? { fontSize: '20px', padding: '8px' } : {}) }}
            onClick={onToggleTrack}
          >
            {isTracked ? '★' : '☆'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: '16px',
  },
  modal: {
    background: '#13151f',
    border: '1.5px solid #2e3347',
    borderRadius: '16px',
    width: '95vw',
    maxWidth: '780px',
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 24px 16px',
    borderBottom: '1px solid #2e3347',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flexShrink: 0,
  },
  setCodeBox: {
    padding: '6px 10px', borderRadius: '8px', border: '1px solid',
    flexShrink: 0, marginTop: '2px',
  },
  productTitle: { margin: 0, fontSize: '18px', fontWeight: 800, color: '#e8eaf0', lineHeight: 1.2 },
  pill: { fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px' },
  closeBtn: {
    background: '#22263a', border: '1px solid #2e3347', color: '#8a93b0',
    cursor: 'pointer', width: '32px', height: '32px', borderRadius: '8px',
    fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, marginLeft: 'auto',
  },
  tabBar: {
    display: 'flex', borderBottom: '1px solid #2e3347',
    padding: '0 24px', flexShrink: 0, background: '#0f1118',
    overflowX: 'auto',
  },
  tabBtn: {
    padding: '12px 16px', background: 'transparent', border: 'none',
    color: '#8a93b0', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
    borderBottom: '2.5px solid transparent', marginBottom: '-1px',
    whiteSpace: 'nowrap', transition: 'color 0.15s',
  },
  tabBtnActive: { color: '#f97316', borderBottomColor: '#f97316' },
  body: {
    flex: 1, overflowY: 'auto', minHeight: 0,
    padding: '0',
  },
  section: {
    padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '10px',
  },
  statBox: {
    background: '#1a1d27', border: '1px solid #2e3347', borderRadius: '10px',
    padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '2px',
  },
  statLabel: { fontSize: '11px', color: '#8a93b0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  statValue: { fontSize: '18px', fontWeight: 800, lineHeight: 1.2 },
  description: { fontSize: '13px', color: '#c4c9db', lineHeight: 1.65, margin: 0 },
  subHeading: { fontSize: '12px', fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' },
  boxContents: {},
  chipRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  contentChip: {
    fontSize: '12px', padding: '4px 10px', borderRadius: '6px',
    background: '#1a1d27', border: '1px solid #2e3347', color: '#c4c9db',
  },
  regionChip: {
    fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px',
    background: '#2e3347', color: '#8a93b0',
  },
  filterChip: {
    padding: '5px 12px', border: '1px solid #2e3347', borderRadius: '20px',
    background: '#1a1d27', color: '#8a93b0', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
  },
  filterChipActive: { borderColor: '#f97316', background: '#f9731620', color: '#f97316' },
  showAllBtn: {
    background: 'transparent', border: 'none', color: '#3b82f6',
    cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: 0,
  },
  storeRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 14px', borderRadius: '10px',
    background: '#1a1d27', border: '1px solid #2e3347',
    transition: 'border-color 0.15s',
  },
  storeRowTracked: { borderColor: '#eab30860', background: '#eab30808' },
  bestBadge: { fontSize: '10px', background: '#22c55e20', color: '#22c55e', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 },
  shieldBadge: { fontSize: '10px', background: '#eab30820', color: '#eab308', padding: '1px 6px', borderRadius: '4px', fontWeight: 700, cursor: 'help' },
  trackedBadge: { fontSize: '10px', background: '#eab30820', color: '#eab308', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 },
  availableBadge: { fontSize: '10px', background: '#22c55e20', color: '#22c55e', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 },
  preorderBadge: { fontSize: '10px', background: '#f9731620', color: '#f97316', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 },
  unlistedBadge: { fontSize: '10px', background: '#6b728020', color: '#8a93b0', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 },
  oosBadge: { fontSize: '10px', background: '#ef444420', color: '#ef4444', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 },
  oosBtn: {
    padding: '5px 12px', background: '#ef444420', border: '1px solid #ef444450',
    borderRadius: '6px', color: '#ef4444', textDecoration: 'none',
    fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
    pointerEvents: 'none' as const, opacity: 0.8,
  },
  verifiedBanner: {
    background: '#22c55e15', border: '1px solid #22c55e40', borderRadius: '8px',
    padding: '8px 12px', fontSize: '12px', color: '#22c55e', fontWeight: 600,
    marginBottom: '8px',
  },
  unverifiedBanner: {
    background: '#eab30815', border: '1px solid #eab30840', borderRadius: '8px',
    padding: '8px 12px', fontSize: '12px', color: '#eab308', lineHeight: 1.45,
    marginBottom: '8px',
  },
  preorderBtn: {
    padding: '5px 12px', background: '#f97316', borderRadius: '6px',
    color: '#fff', textDecoration: 'none', fontSize: '12px', fontWeight: 700,
    whiteSpace: 'nowrap', border: 'none',
  },
  buyBtn: {
    padding: '5px 12px', background: '#22c55e', borderRadius: '6px',
    color: '#fff', textDecoration: 'none', fontSize: '12px', fontWeight: 700,
    whiteSpace: 'nowrap', border: 'none',
  },
  viewBtn: {
    padding: '5px 12px', background: '#f9731615', border: '1px solid #f9731650',
    borderRadius: '6px', color: '#f97316', textDecoration: 'none',
    fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
  },
  starBtn: {
    background: 'transparent', border: '1px solid #2e3347', borderRadius: '6px',
    color: '#8a93b0', cursor: 'pointer', fontSize: '16px',
    width: '30px', height: '30px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 0, flexShrink: 0,
  },
  starBtnActive: { borderColor: '#eab308', color: '#eab308', background: '#eab30815' },
};
